import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/errors";
import { PermissionMatrix, WidgetPicker, useAdminData } from "./adminComponents";
import { TemporaryPasswordField, CredentialResultDialog } from "./passwordControls";
import { SectionHeader } from "@/components/SectionHeader";

const initials = (u) => ((u.first_name?.[0] || "") + (u.last_name?.[0] || "")).toUpperCase() || (u.email || "?")[0].toUpperCase();

// ---------- USERS LIST ----------
export default function AdminUsersPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ department: "", role_class_id: "", status: "active" });
  const { roles } = useAdminData();

  const load = async () => {
    try {
      // F2D · The list handler filters archived by default. `status` opts
      // into archived/all. Client never fetches all and hides — the
      // requested set is what the server returns.
      const params = { q: q || undefined };
      Object.entries(filter).forEach(([k, v]) => { if (v !== "") params[k] = v; });
      const d = await api.get("/admin/users", { params }).then((r) => r.data);
      setRows(d.results);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, filter]);

  const roleLabel = (id) => roles.find((r) => r.id === id)?.label || "—";

  return (
    <div className="p-6" data-testid="admin-users-page">
      {/* RC4 · SectionHeader adoption landmark. */}
      <SectionHeader testid="page-section-landmark" className="sr-only">Users & Roles</SectionHeader>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="font-display text-carbon text-[26px]">Users & Access</h1>
          <div className="text-[12px] text-steel">Manage staff accounts, roles, and per-user permission overrides.</div>
        </div>
        <Link to="/administration/users/new"
              className="inline-flex items-center h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white"
              data-testid="admin-users-new">
          + New User
        </Link>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search name / email / job title"
               data-testid="admin-users-search"
               className="border border-coach-silver rounded-sm text-[12.5px] px-2 py-1 w-72" />
        <select value={filter.department} onChange={(e) => setFilter({ ...filter, department: e.target.value })}
                data-testid="admin-users-filter-department"
                className="border border-coach-silver rounded-sm text-[12.5px] px-2 py-1">
          <option value="">All departments</option>
          {["Executive","Leasing & Driver Operations","Front Office","Accounting","Safety & Compliance","Shop","Administration"].map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filter.role_class_id} onChange={(e) => setFilter({ ...filter, role_class_id: e.target.value })}
                data-testid="admin-users-filter-role"
                className="border border-coach-silver rounded-sm text-[12.5px] px-2 py-1">
          <option value="">All roles</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                data-testid="admin-users-filter-status"
                className="border border-coach-silver rounded-sm text-[12.5px] px-2 py-1">
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="all">All</option>
        </select>
      </div>
      <div className="bg-white border border-coach-silver rounded-md overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left px-3 py-2">Person</th>
              <th className="text-left px-3 py-2">Job Title</th>
              <th className="text-left px-3 py-2">Department</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Login</th>
              <th className="text-center px-3 py-2 w-20">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className={`border-t border-coach-silver/60 hover:bg-road-white cursor-pointer ${u.is_archived ? "opacity-70" : ""}`}
                  data-testid={`admin-user-row-${u.id}`}
                  onClick={() => window.location.assign(`/administration/users/${u.id}`)}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-coach-blue text-white text-[11px] font-semibold grid place-items-center">{initials(u)}</div>
                    <div>
                      <div className="text-carbon font-medium">{u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim()}</div>
                      {u.role === "super_admin" && <span className="inline-block text-[9px] uppercase tracking-widest text-touring-lime border border-touring-lime rounded-sm px-1">Super Admin</span>}
                      {u.role === "admin" && <span className="inline-block text-[9px] uppercase tracking-widest text-coach-blue border border-coach-blue rounded-sm px-1">Admin</span>}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-carbon">{u.job_title || "—"}</td>
                <td className="px-3 py-2 text-carbon">{u.department || "—"}</td>
                <td className="px-3 py-2 text-carbon">{roleLabel(u.role_class_id)}</td>
                <td className="px-3 py-2 font-mono text-[11.5px] text-steel">{u.email}</td>
                <td className="px-3 py-2 text-center">
                  {u.is_archived
                    ? <span data-testid={`admin-user-badge-archived-${u.id}`} className="text-[10.5px] uppercase text-steel border border-coach-silver rounded-sm px-1.5 py-0.5">Archived</span>
                    : <span className="text-[10.5px] uppercase text-coach-blue">Active</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-4 text-center text-steel italic">No users match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- USER DETAIL ----------
export function AdminUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [u, setU] = useState(null);
  const [tab, setTab] = useState("profile");
  const { permCatalog, rootOnlyKeys, widgets, roles, me } = useAdminData();

  const load = async () => {
    try {
      const d = await api.get(`/admin/users/${id}`).then((r) => r.data);
      setU(d);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const roleOf = (rid) => roles.find((r) => r.id === rid);
  const currentRole = roleOf(u?.role_class_id);
  const actorPerms = useMemo(() => new Set(me?.allowed || []), [me]);
  const actorIsSuperAdmin = actorPerms.has("admin.super_admin");
  const targetIsSuper = currentRole?.key === "super_admin";
  const superAdminLocked = !actorIsSuperAdmin && targetIsSuper;

  const save = async (patch) => {
    try {
      const d = await api.patch(`/admin/users/${id}`, patch).then((r) => r.data);
      setU(d);
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!u || !permCatalog) return <div className="p-6 text-steel">Loading…</div>;

  return (
    <div className="p-6" data-testid="admin-user-detail">
      <div className="mb-2 text-[12px]"><Link to="/administration/users" className="text-coach-blue hover:underline">← All users</Link></div>
      <div className="flex items-baseline justify-between mb-3">
        <h1 className="font-display text-carbon text-[24px]">{u.name || `${u.first_name || ""} ${u.last_name || ""}`}</h1>
        <div className="text-[12px] text-steel font-mono">{u.email}</div>
      </div>
      <div className="flex gap-1 mb-4 border-b border-coach-silver">
        {[["profile","Profile"],["access","Access"],["dashboard","Dashboard"],["account","Account"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
                  data-testid={`admin-user-tab-${k}`}
                  className={"px-3 py-1.5 text-[12.5px] " + (tab === k ? "border-b-2 border-coach-blue text-coach-blue font-medium" : "text-steel")}>
            {l}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <ProfileTab u={u} roles={roles} superAdminLocked={superAdminLocked}
                    actorIsSuperAdmin={actorIsSuperAdmin} onSave={save} />
      )}
      {tab === "access" && (
        <AccessTab u={u} catalog={permCatalog} rootOnlyKeys={rootOnlyKeys}
                    roleDefaults={currentRole?.default_permissions || {}}
                    superAdminLocked={superAdminLocked}
                    onSave={save} />
      )}
      {tab === "dashboard" && (
        <DashboardTab u={u} catalog={widgets}
                       roleDefaultConfig={currentRole?.default_dashboard_config || []}
                       actorPerms={actorPerms}
                       onSave={save} />
      )}
      {tab === "account" && (
        <AccountTab u={u} onReload={load} />
      )}
    </div>
  );
}

function ProfileTab({ u, roles, superAdminLocked, actorIsSuperAdmin, onSave }) {
  const [draft, setDraft] = useState({
    first_name: u.first_name || "", last_name: u.last_name || "",
    job_title: u.job_title || "", department: u.department || "",
    role_class_id: u.role_class_id || "", profile_photo_url: u.profile_photo_url || "",
    email: u.email || "",
  });
  const visibleRoles = actorIsSuperAdmin ? roles : roles.filter((r) => r.key !== "super_admin");
  return (
    <div className="bg-white border border-coach-silver rounded-md p-4 max-w-2xl space-y-3">
      {["first_name","last_name","job_title","department","profile_photo_url"].map((k) => (
        <div key={k}>
          <label className="text-[11px] uppercase tracking-widest text-steel">{k.replace(/_/g," ")}</label>
          <input type="text" value={draft[k]} onChange={(e) => setDraft({...draft, [k]: e.target.value})}
                 data-testid={`admin-profile-${k}`}
                 disabled={superAdminLocked}
                 className="mt-0.5 w-full border border-coach-silver rounded-sm text-[13px] px-2 py-1 disabled:opacity-50" />
        </div>
      ))}
      <div>
        <label className="text-[11px] uppercase tracking-widest text-steel">Login email</label>
        <input type="email" value={draft.email}
               onChange={(e) => setDraft({...draft, email: e.target.value})}
               data-testid="admin-profile-email"
               disabled={superAdminLocked}
               className="mt-0.5 w-full border border-coach-silver rounded-sm text-[13px] px-2 py-1 disabled:opacity-50" />
        <div className="text-[10.5px] text-steel mt-1">
          Changing the login email does not change the password.
        </div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-steel">Role class</label>
        <select value={draft.role_class_id} onChange={(e) => setDraft({...draft, role_class_id: e.target.value})}
                data-testid="admin-profile-role_class_id"
                disabled={superAdminLocked}
                className="mt-0.5 w-full border border-coach-silver rounded-sm text-[13px] px-2 py-1 disabled:opacity-50">
          {visibleRoles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        {!actorIsSuperAdmin && <div className="text-[10.5px] text-amber-hold mt-1">Super Admin authorization required to promote to Super Admin.</div>}
      </div>
      <button onClick={() => onSave(draft)} disabled={superAdminLocked}
              data-testid="admin-profile-save"
              className="mt-2 h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-50">
        Save Profile
      </button>
    </div>
  );
}

function AccessTab({ u, catalog, rootOnlyKeys, roleDefaults, superAdminLocked, onSave }) {
  const [overrides, setOverrides] = useState(u.permission_overrides || {});
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button onClick={() => onSave({ permission_overrides: overrides })} disabled={superAdminLocked}
                data-testid="admin-access-save"
                className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-50">
          Save Overrides
        </button>
        <button onClick={() => { setOverrides({}); onSave({ permission_overrides: {} }); }}
                data-testid="admin-access-restore"
                className="h-9 px-4 rounded-sm text-[12.5px] border border-coach-silver hover:bg-road-white">
          Restore All Defaults
        </button>
      </div>
      <PermissionMatrix catalog={catalog} rootOnlyKeys={rootOnlyKeys} roleDefaults={roleDefaults}
                        userOverrides={overrides} mode="user"
                        onChangeOverrides={setOverrides}
                        superAdminLocked={superAdminLocked} />
    </div>
  );
}

function DashboardTab({ u, catalog, roleDefaultConfig, actorPerms, onSave }) {
  const [config, setConfig] = useState(u.dashboard_config ?? null);
  const effective = config ?? roleDefaultConfig;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <button onClick={() => onSave({ dashboard_config: config })}
                data-testid="admin-dashboard-save"
                className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white">
          Save Dashboard
        </button>
        <button onClick={() => { setConfig(null); onSave({ dashboard_config: null }); }}
                data-testid="admin-dashboard-restore"
                className="h-9 px-4 rounded-sm text-[12.5px] border border-coach-silver hover:bg-road-white">
          Restore Role Default
        </button>
        <span className="text-[11px] text-steel italic">
          {config === null ? "Inheriting role default" : "User-specific override"}
        </span>
      </div>
      <WidgetPicker catalog={catalog} selected={effective}
                    onChange={setConfig} allowedPerms={actorPerms} />
    </div>
  );
}

function AccountTab({ u, onReload }) {
  const [showReset, setShowReset] = useState(false);
  const [tempPw, setTempPw] = useState("");
  const [mustChange, setMustChange] = useState(true);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const submitReset = async () => {
    setSaving(true);
    try {
      const body = {
        temporary_password: tempPw.trim() || undefined,
        must_change_password: mustChange,
      };
      const r = await api.post(`/admin/users/${u.id}/reset-password`, body).then((r) => r.data);
      setShowReset(false);
      setTempPw("");
      setResult({ email: r.email, password: r.temporary_password });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doLifecycle = async (action) => {
    // F2D · action = "archive" | "reactivate" | "permanent-delete"
    try {
      if (action === "permanent-delete") {
        if (!window.confirm("Permanently delete this user? This cannot be undone.")) return;
        const r = await api.delete(`/admin/users/${u.id}`).then((r) => r.data);
        toast.success("User permanently deleted");
        window.location.assign("/administration/users");
        return r;
      }
      const path = action === "archive" ? "archive" : "reactivate";
      const r = await api.post(`/admin/users/${u.id}/${path}`).then((r) => r.data);
      toast.success(action === "archive" ? "User archived" : "User reactivated");
      onReload();
      return r;
    } catch (e) {
      // Refs summary: server may return {detail: {message, references: {...}}}
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.references) {
        const lines = Object.entries(d.references).map(([k, v]) => `  · ${k}: ${v}`).join("\n");
        toast.error(`${d.message}\n${lines}`, { duration: 8000 });
      } else {
        toast.error(apiError(e));
      }
    }
  };

  return (
    <div className="bg-white border border-coach-silver rounded-md p-4 max-w-2xl space-y-3">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-steel">Current status</div>
        <div className="text-carbon">{u.is_archived ? "Archived" : "Active"}</div>
        {u.is_archived && u.archived_at && (
          <div className="text-[11px] text-steel mt-1">
            Archived at {String(u.archived_at).replace("T"," ").slice(0,19)}
          </div>
        )}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-steel">Password state</div>
        <div className="text-carbon">
          {u.must_change_password
            ? "Awaiting first-login password change"
            : "User-managed"}
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setShowReset(true)}
                data-testid="admin-account-reset-password"
                className="h-9 px-4 rounded-sm text-[12.5px] font-semibold border border-coach-silver hover:bg-road-white">
          Reset Password
        </button>
        {u.is_archived
          ? <>
              <button onClick={() => doLifecycle("reactivate")}
                      data-testid="admin-account-reactivate"
                      className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white">Reactivate</button>
              <button onClick={() => doLifecycle("permanent-delete")}
                      data-testid="admin-account-permanent-delete"
                      className="h-9 px-4 rounded-sm text-[12.5px] font-semibold border border-critical-red text-critical-red hover:bg-critical-red hover:text-white">Delete Permanently</button>
            </>
          : <button onClick={() => doLifecycle("archive")}
                    data-testid="admin-account-archive"
                    className="h-9 px-4 rounded-sm text-[12.5px] font-semibold border border-critical-red text-critical-red hover:bg-critical-red hover:text-white">Archive</button>}
      </div>
      <div className="text-[10.5px] text-steel italic">
        Archiving hides the account from lists and selectors and prevents login.
        Permanent deletion is only available for archived users with no historical
        references (audit trail, quotes, contracts, tours, etc.).
      </div>

      {showReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
             data-testid="reset-password-dialog">
          <div className="bg-white border border-coach-silver rounded-md shadow-xl max-w-lg w-full">
            <div className="bg-carbon text-white px-5 py-3 text-[12px] uppercase tracking-widest">
              Reset password for {u.name || u.email}
            </div>
            <div className="p-5 space-y-4 text-[13px]">
              <div className="text-carbon">
                The user's current password will be invalidated immediately.
                Their profile, role, permissions, and history are preserved.
              </div>
              <TemporaryPasswordField value={tempPw} onChange={setTempPw}
                                      testidPrefix="reset-pw" autoFocus />
              <label className="flex items-center gap-2 text-[12px] text-carbon">
                <input type="checkbox" checked={mustChange}
                       onChange={(e) => setMustChange(e.target.checked)}
                       data-testid="reset-pw-must-change" />
                Require password change at next login
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => { setShowReset(false); setTempPw(""); }}
                        data-testid="reset-pw-cancel"
                        className="h-9 px-3 rounded-sm text-[12px] border border-coach-silver hover:bg-road-white">
                  Cancel
                </button>
                <button onClick={submitReset} disabled={saving}
                        data-testid="reset-pw-submit"
                        className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-critical-red hover:opacity-90 text-white disabled:opacity-60">
                  {saving ? "Resetting…" : "Reset Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CredentialResultDialog open={!!result} onClose={() => { setResult(null); onReload(); }}
                              email={result?.email} password={result?.password}
                              actionLabel="password reset" />
    </div>
  );
}
